import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DeleteBusiness, TransferOwnership } from './OwnershipSections';
import { ModulesSettings } from './ModulesSettings';
import { IntegrationSettings } from './IntegrationSettings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Lock, MapPin, Plus, ScrollText, Trash2 } from 'lucide-react';
import {
  OFFERING_KINDS,
  OFFERING_KIND_LABELS,
  type OfferingKind,
  BUSINESS_TYPE_LABELS,
  COUNTRIES,
  CURRENCIES,
  ASSIGNABLE_MEMBER_ROLES,
  MEMBER_ROLES,
  MEMBER_ROLE_LABELS,
  canManageRole,
  findCountry,
  type AuditLog,
  type Branch,
  type Business,
  type BusinessType,
  type Member,
  type MemberRole,
  type OperatingCountries,
  type Paginated,
} from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { roleAtLeast } from '@cenox/shared';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { relativeTime } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  EmptyState,
  Input,
  Modal,
  Select,
  Spinner,
  cn,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

/**
 * Locations used to live here as "Branches". They now have their own screen
 * under Shop, because they are operational rather than configuration — and two
 * screens managing one thing is how the two drift apart.
 */
type Tab = 'business' | 'team' | 'modules' | 'integrations' | 'activity';

export function SettingsPage(): ReactNode {
  const { can, business, memberships, selectBusiness } = useAuth();
  const role = business?.role ?? 'worker';
  const [params] = useSearchParams();
  /*
    Deep links from the account page: `?business=<id>&tab=team` opens the right
    business on the right section, so "Transfer ownership" lands where the
    transfer actually is.
  */
  const wantedBusiness = params.get('business');
  useEffect(() => {
    if (
      wantedBusiness &&
      wantedBusiness !== business?.businessId &&
      memberships.some((m) => m.businessId === wantedBusiness)
    ) {
      selectBusiness(wantedBusiness);
    }
  }, [wantedBusiness, business?.businessId, memberships, selectBusiness]);
  const linkedTab = params.get('tab');
  const [chosen, setTab] = useState<Tab | null>(
    linkedTab === 'team' ||
    linkedTab === 'business' ||
    linkedTab === 'modules' ||
    linkedTab === 'integrations' ||
    linkedTab === 'activity'
      ? linkedTab
      : null,
  );

  const tabs: Array<{ id: Tab; label: string; visible: boolean }> = [
    { id: 'business', label: 'Business', visible: can('settings:read') },
    { id: 'team', label: 'Team', visible: can('members:read') },
    { id: 'modules', label: 'Modules', visible: can('settings:read') },
    /*
      Beside Modules rather than under Team: a credential is not a person, and
      filing it with the staff list is how somebody eventually tries to give
      one a job title.
    */
    {
      id: 'integrations',
      label: 'Integrations',
      /*
        Owner and admin, matching the API.

        Staff hold `settings:read` and can see most of this screen's
        neighbours, but a credential list says which systems write into this
        business and when each last connected. Offering staff a tab the server
        refuses would also be a worse experience than not offering it.
      */
      visible: roleAtLeast(role, 'admin'),
    },

    { id: 'activity', label: 'Activity log', visible: can('audit:read') },
  ];

  const visible = tabs.filter((t) => t.visible);

  /**
   * Show a tab only if this login may see it.
   *
   * The tab list was already filtered, but the selection was not: it started
   * on 'business' whatever the filter said, so somebody without
   * `settings:read` landed on a section with no tab to leave it by, and saw
   * the API's refusal rendered as a failure. The server was right; the screen
   * was asking a question it had no business asking.
   */
  const tab = chosen && visible.some((t) => t.id === chosen) ? chosen : (visible[0]?.id ?? null);

  return (
    <>
      <PageHeader title="Settings" description="Business details, team and activity" />

      <div className="mb-5 border-b border-slate-200">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Settings sections">
          {visible.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              aria-current={tab === item.id ? 'page' : undefined}
              className={cn(
                'whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
                tab === item.id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900',
              )}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'business' && (
        <>
          <BusinessSettings />
          <DeleteBusiness />
        </>
      )}
      {tab === 'team' && <TeamSettings />}
      {tab === 'modules' && <ModulesSettings onManage={(next) => setTab(next as Tab)} />}
      {tab === 'integrations' && <IntegrationSettings />}

      {tab === 'activity' && <ActivityLog />}

      {tab === null && (
        <EmptyState
          title="Nothing here for you"
          description="Settings are managed by the people who run this business."
        />
      )}
    </>
  );
}

// ── Business ────────────────────────────────────────────────────────────────

function BusinessSettings(): ReactNode {
  const { business, can, reload } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['business', business?.businessId],
    queryFn: () => api.get<Business>(path('')),
    enabled: Boolean(business),
  });

  const [form, setForm] = useState<Partial<Business>>({});
  const [saved, setSaved] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.patch<Business>(path(''), payload),
    onSuccess: async () => {
      setSaved(true);
      setFieldErrors({});
      await reload();
      void queryClient.invalidateQueries({ queryKey: ['business'] });
      // Currency or timezone changes ripple through every figure on screen.
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.fields) {
        setFieldErrors(
          Object.fromEntries(
            Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
          ),
        );
      }
    },
  });

  if (isPending) return <Spinner />;
  /*
    Say what the server said.

    "Could not load business details" on its own is a dead end: it reads like a
    permission problem whatever actually went wrong, and the one thing it hides
    is the sentence that would have explained it.
  */
  if (error)
    return (
      <ErrorState
        message={
          error instanceof ApiError && error.message
            ? `Could not load business details: ${error.message}`
            : 'Could not load business details.'
        }
        onRetry={() => refetch()}
      />
    );

  const value = <K extends keyof Business>(key: K): Business[K] => form[key] ?? data[key];
  const editable = can('settings:update');

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    save.mutate(form as Record<string, unknown>);
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader title="Business details" subtitle="Shown on reports and exports" />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Input
          label="Business name"
          value={String(value('name'))}
          disabled={!editable}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          error={fieldErrors.name}
        />

        {/*
          Chosen when the business was created, and never changed after: the
          server refuses it and the database will not store it (0155). So it
          is shown, never offered.
        */}
        <div data-testid="business-type">
          <span className="mb-1 block text-sm font-medium text-slate-700" id="business-type-label">Business type</span>
          <div
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
            aria-labelledby="business-type-label"
            aria-readonly="true"
            role="textbox"
          >
            <Lock className="size-4 shrink-0 text-slate-500" aria-hidden />
            <span>{BUSINESS_TYPE_LABELS[data.type as BusinessType] ?? data.type}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">Business type is permanently locked after account creation.</p>
        </div>

        {/*
          A separate question from the type: a Delivery & Logistics business
          sells services; a manufacturer may sell both.
        */}
        <Select
          label="What do you sell?"
          options={OFFERING_KINDS.map((v) => ({ value: v, label: OFFERING_KIND_LABELS[v] }))}
          value={String(value('offeringKind'))}
          disabled={!editable}
          onChange={(e) => setForm((f) => ({ ...f, offeringKind: e.target.value as OfferingKind }))}
          error={fieldErrors.offeringKind}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Country"
            options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
            value={String(value('countryCode'))}
            disabled={!editable}
            onChange={(e) => setForm((f) => ({ ...f, countryCode: e.target.value }))}
            error={fieldErrors.countryCode}
          />
          <Select
            label="Default currency"
            options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
            value={String(value('currencyCode'))}
            disabled={!editable}
            onChange={(e) => setForm((f) => ({ ...f, currencyCode: e.target.value }))}
            error={fieldErrors.currencyCode}
            hint="Applies to new records. Existing records keep the currency they were recorded in."
          />
        </div>

        <Input
          label="Timezone"
          value={String(value('timezone'))}
          disabled={!editable}
          onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
          error={fieldErrors.timezone}
          hint="Decides when your business day starts and ends on the dashboard"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Phone"
            value={String(value('phone') ?? '')}
            disabled={!editable}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            error={fieldErrors.phone}
          />
          <Input
            label="Email"
            type="email"
            value={String(value('email') ?? '')}
            disabled={!editable}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            error={fieldErrors.email}
          />
        </div>

        {editable && (
          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" loading={save.isPending} disabled={Object.keys(form).length === 0}>
              Save changes
            </Button>
            {saved && <span className="text-sm font-medium text-emerald-600">Saved</span>}
          </div>
        )}
      </form>
    </Card>
  );
}

// ── Team ────────────────────────────────────────────────────────────────────

function TeamSettings(): ReactNode {
  const { business, can, user } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [assigning, setAssigning] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['members', business?.businessId],
    queryFn: () => api.get<{ data: Member[] }>(path('/members')),
    enabled: Boolean(business),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(path(`/members/${id}`)),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (caught) => {
      // Already gone (removed from another tab): the list is simply stale.
      if (caught instanceof ApiError && caught.status === 404) {
        setRemoving(null);
        void queryClient.invalidateQueries({ queryKey: ['members'] });
        return;
      }
      setRemoveError(caught instanceof ApiError ? caught.message : 'Could not remove this member.');
    },
  });

  /** Who this login may remove: below their own role, and never themselves. */
  const removable = (member: Member): boolean =>
    can('members:remove') &&
    member.role !== 'owner' &&
    member.userId !== user?.id &&
    business !== null &&
    canManageRole(business.role as MemberRole, member.role);

  if (isPending) return <Spinner />;
  if (error) return <ErrorState message="Could not load the team." onRetry={() => refetch()} />;

  return (
    <>
      <Card padded={false} className="max-w-3xl">
        <div className="p-4 sm:p-5">
          <CardHeader
            title="Team"
            subtitle="Who can sign in to this business"
            action={
              can('members:invite') && (
                <Button
                  size="sm"
                  icon={<Plus className="size-4" />}
                  onClick={() => setInviting(true)}
                >
                  Invite
                </Button>
              )
            }
          />
        </div>

        <ul className="divide-y divide-slate-100 border-t border-slate-200">
          {data.data.map((member) => (
            <li key={member.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {member.fullName ?? member.email}
                  {member.userId === user?.id && (
                    <span className="ml-1.5 text-xs font-normal text-slate-500">(you)</span>
                  )}
                </p>
                <p className="truncate text-xs text-slate-500">{member.email}</p>
              </div>

              <Badge tone={member.role === 'owner' ? 'info' : 'neutral'}>
                {MEMBER_ROLE_LABELS[member.role]}
              </Badge>

              {member.status === 'invited' && <Badge tone="warning">Invited</Badge>}

              {can('members:update') && member.role !== 'owner' && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<MapPin className="size-4" />}
                  onClick={() => setAssigning(member)}
                >
                  Locations
                </Button>
              )}

              {removable(member) && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 className="size-4" />}
                  aria-label={`Remove ${member.email}`}
                  onClick={() => {
                    setRemoveError(null);
                    setRemoving(member);
                  }}
                >
                  Remove
                </Button>
              )}

              {can('members:remove') && member.role === 'owner' && (
                <span
                  className="text-xs text-slate-400"
                  title="The owner cannot be removed. Transfer ownership to someone else first."
                >
                  Transfer ownership to remove
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {business?.role === 'owner' && (
        <p className="text-sm text-slate-600">
          Loan permissions for each person are set under{' '}
          <Link to="/loans/settings" className="font-medium text-brand-700 hover:underline">Loans → Settings</Link>.
        </p>
      )}

      <TransferOwnership members={data.data} />

      <Modal open={removing !== null} onClose={() => setRemoving(null)} title="Remove member">
        {removing && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[6rem_1fr] gap-2 text-sm">
              <dt className="text-slate-500">Name</dt>
              <dd className="text-slate-900">{removing.fullName ?? '—'}</dd>
              <dt className="text-slate-500">Email</dt>
              <dd className="break-all text-slate-900">{removing.email}</dd>
              <dt className="text-slate-500">Role</dt>
              <dd className="text-slate-900">{MEMBER_ROLE_LABELS[removing.role]}</dd>
            </dl>
            <p className="text-sm text-slate-600">
              They lose access to {business?.businessName} immediately. Their CenoX account, their
              other workspaces and everything they recorded here stay as they are.
            </p>
            {removeError && (
              <p role="alert" className="text-sm text-red-700">
                {removeError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => remove.mutate(removing.id)}
              >
                Remove {removing.fullName ?? removing.email}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={assigning !== null}
        onClose={() => setAssigning(null)}
        title="Where this person works"
      >
        {assigning && (
          <MemberLocations member={assigning} onClose={() => setAssigning(null)} />
        )}
      </Modal>

      <Modal open={inviting} onClose={() => setInviting(false)} title="Invite a team member">
        <InviteForm
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['members'] });
          }}
          onCancel={() => setInviting(false)}
        />
      </Modal>
    </>
  );
}

/**
 * Which locations a login may work in.
 *
 * Deliberately about MEMBERS, not staff records. A staff record is a person the
 * business employs; a member is a login. Only the second can open a worker link
 * and record anything, and only the second is what row level security consults
 * — so assigning a staff record to Holy Ghost, which the Locations screen does,
 * grants nobody access to anything.
 *
 * Until this screen existed the only way to grant it was the API, which meant
 * the link an owner copied could not actually be made to work from the
 * dashboard. That is the gap this closes.
 */
function MemberLocations({
  member,
  onClose,
}: {
  member: Member;
  onClose: () => void;
}): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const locations = useQuery({
    queryKey: ['branches', business?.businessId],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string; isActive: boolean }> }>(
      path('/branches'),
    ),
    enabled: Boolean(business),
  });

  const current = useQuery({
    queryKey: ['member-locations', member.id],
    queryFn: () =>
      api.get<{ scope: 'all' | 'assigned'; branches: Array<{ branchId: string }> }>(
        path(`/members/${member.id}/locations`),
      ),
  });

  const [scope, setScope] = useState<'all' | 'assigned' | null>(null);
  const [chosen, setChosen] = useState<string[] | null>(null);

  // Start from what is stored, once it arrives.
  const effectiveScope = scope ?? current.data?.scope ?? 'assigned';
  const effectiveChosen = chosen ?? current.data?.branches.map((b) => b.branchId) ?? [];

  const save = useMutation({
    mutationFn: () =>
      api.put(path(`/members/${member.id}/locations`), {
        scope: effectiveScope,
        branchIds: effectiveScope === 'all' ? [] : effectiveChosen,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['member-locations', member.id] });
      void queryClient.invalidateQueries({ queryKey: ['members'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save those locations.'),
  });

  if (locations.isPending || current.isPending || !locations.data || !current.data) {
    return <Spinner label="Loading locations" />;
  }

  const toggle = (id: string): void =>
    setChosen(
      effectiveChosen.includes(id)
        ? effectiveChosen.filter((x) => x !== id)
        : [...effectiveChosen, id],
    );

  return (
    <div className="space-y-4">
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <p className="text-sm text-slate-600">
        {member.fullName ?? member.email} can work in:
      </p>

      <div className="space-y-2">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="scope"
            className="mt-1"
            checked={effectiveScope === 'all'}
            onChange={() => setScope('all')}
          />
          <span>
            <span className="font-medium text-slate-900">Every location</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Including any location added later
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="scope"
            className="mt-1"
            checked={effectiveScope === 'assigned'}
            onChange={() => setScope('assigned')}
          />
          <span>
            <span className="font-medium text-slate-900">Only the locations I choose</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              They see nothing from anywhere else, including through a worker link
            </span>
          </span>
        </label>
      </div>

      {effectiveScope === 'assigned' && (
        <ul className="space-y-1 rounded-lg border border-slate-200 p-3">
          {locations.data.data
            .filter((l) => l.isActive)
            .map((location) => (
              <li key={location.id}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={effectiveChosen.includes(location.id)}
                    onChange={() => toggle(location.id)}
                  />
                  {location.name}
                </label>
              </li>
            ))}
          {effectiveChosen.length === 0 && (
            <li className="pt-1 text-xs text-amber-700">
              With nothing selected they can see no business data at all.
            </li>
          )}
        </ul>
      )}

      <div className="flex gap-2">
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          Save locations
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function InviteForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('staff');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const invite = useMutation({
    mutationFn: (payload: { email: string; role: MemberRole }) =>
      api.post<{ member: Member; invitationToken: string }>(path('/members'), payload),
    onSuccess: (result) => {
      setToken(result.invitationToken);
      setError(null);
      onDone();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : 'Could not send the invitation.');
    },
  });

  // Only roles strictly below the inviter's own can be assigned, mirroring the
  // server's `canManageRole` guard so we do not offer a choice that will fail.
  const assignable = ASSIGNABLE_MEMBER_ROLES.filter((candidate) => {
    if (!business) return false;
    return MEMBER_ROLES.indexOf(business.role) > MEMBER_ROLES.indexOf(candidate);
  });

  if (token) {
    // /join/:token, which is the page that actually redeems it. This used to
    // point at /register?invite=…, a query parameter nothing ever read — so
    // the recipient registered a bare account and never joined the business.
    const link = `${window.location.origin}/join/${token}`;
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <p className="text-sm text-emerald-900">
            Invitation created. Email delivery is not connected yet, so share this link yourself —
            it is shown only once.
          </p>
        </div>

        <div className="flex gap-2">
          <input readOnly value={link} className="input-base font-mono text-xs" />
          <Button
            variant="secondary"
            icon={<Copy className="size-4" />}
            onClick={() => {
              void navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>

        <div className="flex justify-end">
          <Button onClick={onCancel}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        invite.mutate({ email, role });
      }}
      className="space-y-4"
      noValidate
    >
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        label="Email"
        type="email"
        required
        autoFocus
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />

      <Select
        label="Role"
        required
        options={assignable.map((value) => ({ value, label: MEMBER_ROLE_LABELS[value] }))}
        value={role}
        onChange={(event) => setRole(event.target.value as MemberRole)}
        hint="Staff can record sales and expenses. Admins can also manage the team and reports."
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={invite.isPending}>
          Create invitation
        </Button>
      </div>
    </form>
  );
}

// ── Branches ────────────────────────────────────────────────────────────────
//
// A branch is where a business stops being single-country, so this is where the
// three locality fields live. All three are optional and all three default to
// "inherit the business", which is what a single-location shop always means —
// the form never asks an owner with one branch to answer a question about
// countries, and the list only starts talking about them once a branch has an
// answer of its own.


/**
 * Where a branch operates, in one line.
 *
 * An inherited value is shown, not hidden — the owner still needs to know a
 * branch is treated as Nigerian — but it is named as inherited, so nobody
 * mistakes a default for a decision someone made about that location.
 */
function localitySummary(branch: Branch): string {
  const country = findCountry(branch.effectiveCountryCode)?.name ?? branch.effectiveCountryCode;

  const own = [branch.countryCode, branch.timezone, branch.currencyCode].some(Boolean);
  const suffix = own ? '' : ' · inherited from the business';

  return `${country} · ${branch.effectiveTimezone} · ${branch.effectiveCurrencyCode}${suffix}`;
}


/** Empty string is the wire's `null`: "inherit the business". */
const INHERIT = '';


// ── Activity log ────────────────────────────────────────────────────────────

function ActivityLog(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const [page, setPage] = useState(1);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['audit', business?.businessId, page],
    queryFn: () => api.get<Paginated<AuditLog>>(path(`/audit-logs${qs({ page, pageSize: 30 })}`)),
    enabled: Boolean(business),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorState message="Could not load the activity log." onRetry={() => refetch()} />;

  return (
    <Card padded={false} className="max-w-3xl">
      <div className="p-4 sm:p-5">
        <CardHeader
          title="Activity log"
          /*
            The log outgrew its own description. It has always carried the
            financial record; since project sharing it also carries who was
            given access to whose conversations, which is not a financial
            change and is exactly the kind of entry somebody comes here to
            find. Saying only "financial" made the access entries look like
            they had wandered in by mistake.
          */
          subtitle="Changes to financial records, and who was given access to what — with who did it"
        />
      </div>

      {data.data.length === 0 ? (
        <p className="px-5 pb-8 text-center text-sm text-slate-500">No activity recorded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 border-t border-slate-200">
          {data.data.map((entry) => (
            <li key={entry.id} className="flex gap-3 px-4 py-3 sm:px-5">
              <ScrollText className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-900">{entry.summary ?? entry.action}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {entry.actorName ?? entry.actorEmail ?? 'System'} ·{' '}
                  {relativeTime(entry.createdAt)}
                  {entry.changedFields.length > 0 && ` · changed ${entry.changedFields.join(', ')}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {data.meta.total > data.meta.pageSize && (
        <div className="flex justify-between border-t border-slate-200 px-4 py-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= data.meta.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </Card>
  );
}
